import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';

const DistributorSummaryTable = ({
  data = [],
  page = 1,
  limit = 20,
  loading = false,
  onSortChange,
  orderBy = 'grossBusiness',
  orderDir = 'DESC',
  columnFilters = {},
  onColumnFilterChange,
  onApplyFilters,
}) => {
  const navigate = useNavigate();
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Double scroll sync
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };
    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [data]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleSort = (columnKey) => {
    if (onSortChange) {
      const nextDir = orderBy === columnKey && orderDir === 'DESC' ? 'ASC' : 'DESC';
      onSortChange(columnKey, nextDir);
    }
  };

  const getSortIcon = (columnKey) => {
    if (orderBy !== columnKey) return faSort;
    return orderDir === 'ASC' ? faSortUp : faSortDown;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onApplyFilters();
    }
  };

  const handleDistributorClick = (distributor) => {
    if (distributor?.id) {
      navigate(`/Report/Distributor/${distributor.id}`, {
        state: { distributor },
      });
    }
  };

  return (
    <div className="panel panel-default with-rounded-corner with-shadow" style={{ marginBottom: 0 }}>
      {/* Double Scroll Container */}
      <div className="large-table-container-3">
        {/* Top Scrollbar */}
        <div
          ref={topScrollbarRef}
          onScroll={handleTopScroll}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            height: '14px',
            marginBottom: '1px',
            background: '#f8f9fa',
          }}
        >
          <div ref={topScrollbarContentRef} style={{ height: '1px' }}></div>
        </div>

        {/* Table Container */}
        <div
          ref={tableContainerRef}
          onScroll={syncScroll}
          style={{
            overflowY: 'auto',
            height: 'calc(112vh - 250px)',
            overflowX: 'auto',
            position: 'relative',
          }}
        >
          <table className="table table-condensed table-hover bg-white sticky-table" style={{ fontSize: '13px', marginBottom: 0 }}>
            <thead>
              {/* Row 1: Clickable Columns with Sort */}
              <tr style={{ cursor: 'pointer', background: '#f4f6f8' }}>
                <th style={{ width: '45px' }}>#</th>
                <th onClick={() => handleSort('name')}>
                  Distributor Name <FontAwesomeIcon icon={getSortIcon('name')} />
                </th>
                <th>Referral Code</th>
                <th>Mobile</th>
                <th>Email</th>
                <th onClick={() => handleSort('commissionPercentage')}>
                  Comm % <FontAwesomeIcon icon={getSortIcon('commissionPercentage')} />
                </th>
                <th onClick={() => handleSort('totalOrders')}>
                  Paid Orders <FontAwesomeIcon icon={getSortIcon('totalOrders')} />
                </th>
                <th onClick={() => handleSort('conversionRate')}>
                  Rate <FontAwesomeIcon icon={getSortIcon('conversionRate')} />
                </th>
                <th onClick={() => handleSort('grossBusiness')}>
                  Gross Sales (₹) <FontAwesomeIcon icon={getSortIcon('grossBusiness')} />
                </th>
                <th onClick={() => handleSort('totalCommission')}>
                  Total Comm (₹) <FontAwesomeIcon icon={getSortIcon('totalCommission')} />
                </th>
              </tr>

              {/* Row 2: Inline Column Filters */}
              <tr>
                <th></th>
                <th>
                  <div className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      placeholder="Search name..."
                      value={columnFilters.name || ''}
                      onChange={(e) => onColumnFilterChange('name', e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={{ width: '130px', padding: '3px 6px', fontSize: '11.5px', borderRadius: '3px', border: '1px solid #ccc' }}
                    />
                    <button type="button" className="btn btn-default btn-xs" onClick={onApplyFilters}>
                      <FontAwesomeIcon icon={faSearch} />
                    </button>
                  </div>
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Code..."
                    value={columnFilters.referralCode || ''}
                    onChange={(e) => onColumnFilterChange('referralCode', e.target.value)}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '90px', padding: '3px 6px', fontSize: '11.5px', borderRadius: '3px', border: '1px solid #ccc' }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Mobile..."
                    value={columnFilters.mobile || ''}
                    onChange={(e) => onColumnFilterChange('mobile', e.target.value)}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '100px', padding: '3px 6px', fontSize: '11.5px', borderRadius: '3px', border: '1px solid #ccc' }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Email..."
                    value={columnFilters.email || ''}
                    onChange={(e) => onColumnFilterChange('email', e.target.value)}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '120px', padding: '3px 6px', fontSize: '11.5px', borderRadius: '3px', border: '1px solid #ccc' }}
                  />
                </th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, rIdx) => (
                  <tr key={`skel-row-${rIdx}`} className="skeleton-row">
                    {Array.from({ length: 10 }).map((_, cIdx) => (
                      <td key={`skel-cell-${cIdx}`}>
                        <div className="skeleton-text" style={{ width: '80%', height: '14px' }}></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length > 0 ? (
                data.map((row, idx) => (
                  <tr
                    key={row.id || idx}
                    onClick={() => handleDistributorClick(row)}
                    style={{ cursor: 'pointer' }}
                    title="Click row to view full distributor details and analytics"
                  >
                    <td>{(page - 1) * limit + idx + 1}</td>
                    <td>
                      <span style={{ color: '#2563eb', fontWeight: '500' }}>
                        {row.name}
                      </span>
                    </td>
                    <td>{row.referralCode}</td>
                    <td>{row.mobile || '—'}</td>
                    <td>{row.email || '—'}</td>
                    <td>{row.commissionPercentage}%</td>
                    <td>{Number(row.totalOrders || 0).toLocaleString()}</td>
                    <td>{row.conversionRate || 0}%</td>
                    <td>₹{Number(row.grossBusiness || 0).toLocaleString()}</td>
                    <td>₹{Number(row.totalCommission || 0).toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center" style={{ padding: '30px', color: '#888' }}>
                    No distributor summary records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DistributorSummaryTable;
